import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Building2, Mail, Lock, User, Link, Save, X, AlertCircle, Check, Loader2, Upload, Eye, EyeOff } from 'lucide-react'
import { z } from 'zod'
import { useCreateCompany } from '../../hooks/useCreateCompany'

// Zod schema for form validation
export const createCompanySchema = z.object({
  companyName: z.string().min(2, 'Company name must be at least 2 characters').max(255, 'Company name must be less than 255 characters'),
  description: z.string().max(2000, 'Description must be less than 2000 characters').optional(),
  website: z.string().url('Must be a valid URL').optional().or(z.literal('')),
  logoUrl: z.string().url('Must be a valid URL').optional().or(z.literal('')),
  adminEmail: z.string().email('Must be a valid email address'),
  adminPassword: z.string().min(8, 'Password must be at least 8 characters'),
  adminName: z.string().min(2, 'Admin name must be at least 2 characters').max(255, 'Admin name must be less than 255 characters'),
  // New fields from E18-S3
  employeeCount: z.number().int().nonnegative('Employee count must be a non-negative number'),
  revenueRange: z.enum(['under-1M', '1M-5M', '5M-10M', '10M-50M', '50M-100M', '100M+']).optional(),
  businessBranches: z.string().max(2000, 'Business branches must be less than 2000 characters').optional(),
  aiPainPoints: z.string().min(20, 'AI pain points must be at least 20 characters').max(2000, 'AI pain points must be less than 2000 characters'),
  aiAdoptionStage: z.enum(['exploring', 'piloting', 'implementing', 'scaling', 'mature']).optional()
})

export type CreateCompanyFormData = z.infer<typeof createCompanySchema>

interface CreateCompanyFormProps {
  onSuccess?: () => void
  onCancel?: () => void
}

export function CreateCompanyForm({
  onSuccess,
  onCancel
}: CreateCompanyFormProps) {
  const [formData, setFormData] = useState<CreateCompanyFormData>({
    companyName: '',
    description: '',
    website: '',
    logoUrl: '',
    adminEmail: '',
    adminPassword: '',
    adminName: '',
    // New fields from E18-S3
    employeeCount: 0,
    revenueRange: undefined,
    businessBranches: '',
    aiPainPoints: '',
    aiAdoptionStage: undefined
  })

  const [errors, setErrors] = useState<Record<string, string>>({})
  const [showPassword, setShowPassword] = useState(false)

  const { createCompany, loading, error } = useCreateCompany()

  const [showSuccess, setShowSuccess] = useState(false)
  const [successMessage, setSuccessMessage] = useState('')

  const validateField = (field: keyof CreateCompanyFormData, value: any) => {
    try {
      const partialSchema = createCompanySchema.pick({ [field]: true as never })
      partialSchema.parse({ [field]: value })
      setErrors(prev => ({ ...prev, [field]: '' }))
      return true
    } catch (error) {
      if (error instanceof z.ZodError) {
        setErrors(prev => ({
          ...prev,
          [field]: error.issues[0]?.message || 'Invalid value'
        }))
      }
      return false
    }
  }

  const handleInputChange = (
    field: keyof CreateCompanyFormData,
    value: string | number | undefined
  ) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    if (typeof value === 'string' || typeof value === 'number') {
      validateField(field, value)
    }
  }

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setErrors(prev => ({ ...prev, logoUrl: 'Only image files are allowed' }))
      return
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setErrors(prev => ({ ...prev, logoUrl: 'File size must be less than 5MB' }))
      return
    }

    // For now, just store the file name
    // In production, you'd upload to a server and get a URL back
    setFormData(prev => ({ ...prev, logoUrl: file.name }))
    setErrors(prev => ({ ...prev, logoUrl: '' }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Validate all fields
    try {
      createCompanySchema.parse(formData)
      setErrors({})
    } catch (error) {
      if (error instanceof z.ZodError) {
        const newErrors: Record<string, string> = {}
        error.issues.forEach((err: any) => {
          if (err.path[0]) {
            newErrors[err.path[0] as string] = err.message
          }
        })
        setErrors(newErrors)
        return
      }
    }

    try {
      await createCompany(formData)

      setSuccessMessage('Company created successfully! Admin account has been set up.')
      setShowSuccess(true)

      setTimeout(() => {
        setShowSuccess(false)
        onSuccess?.()
      }, 2000)
    } catch (err: any) {
      // Error is already handled by the hook
      console.error('Failed to create company:', err)
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-4xl mx-auto"
    >
      <div className="bg-card rounded-lg border border-border p-6">
        {/* Header */}
        <div className="mb-6">
          <h2 className="text-2xl font-bold mb-2 flex items-center gap-2">
            <Building2 className="w-6 h-6" />
            Create New Company
          </h2>
          <p className="text-muted-foreground">
            Add a new company to the platform and create its admin account
          </p>
        </div>

        {/* Success Toast */}
        <AnimatePresence>
          {showSuccess && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="mb-6 p-4 rounded-lg flex items-center gap-3 bg-green-500/10 text-green-600 border border-green-500/20"
            >
              <Check className="w-5 h-5 flex-shrink-0" />
              <p>{successMessage}</p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Error Banner */}
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="mb-6 p-4 rounded-lg flex items-center gap-3 bg-red-500/10 text-red-600 border border-red-500/20"
            >
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <p>{error}</p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Company Information Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              Company Information
            </h3>

            {/* Company Name (Required) */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Company Name <span className="text-destructive">*</span>
              </label>
              <input
                type="text"
                value={formData.companyName}
                onChange={(e) => handleInputChange('companyName', e.target.value)}
                className={`w-full px-4 py-2 rounded-lg border bg-background ${
                  errors.companyName ? 'border-destructive' : 'border-border'
                } focus:outline-none focus:ring-2 focus:ring-primary`}
                placeholder="Enter company name"
                disabled={loading}
              />
              {errors.companyName && (
                <p className="mt-1 text-sm text-destructive flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.companyName}
                </p>
              )}
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                rows={3}
                className={`w-full px-4 py-2 rounded-lg border bg-background ${
                  errors.description ? 'border-destructive' : 'border-border'
                } focus:outline-none focus:ring-2 focus:ring-primary resize-none`}
                placeholder="Enter a brief description of the company"
                disabled={loading}
              />
              {errors.description && (
                <p className="mt-1 text-sm text-destructive flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.description}
                </p>
              )}
              <p className="mt-1 text-sm text-muted-foreground">
                {formData.description?.length || 0} / 2000 characters
              </p>
            </div>

            {/* Website */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Website
              </label>
              <div className="relative">
                <Link className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  type="url"
                  value={formData.website}
                  onChange={(e) => handleInputChange('website', e.target.value)}
                  className={`w-full pl-10 pr-4 py-2 rounded-lg border bg-background ${
                    errors.website ? 'border-destructive' : 'border-border'
                  } focus:outline-none focus:ring-2 focus:ring-primary`}
                  placeholder="https://example.com"
                  disabled={loading}
                />
              </div>
              {errors.website && (
                <p className="mt-1 text-sm text-destructive flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.website}
                </p>
              )}
            </div>

            {/* Logo Upload */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Company Logo
              </label>
              <div className="flex items-center gap-3">
                <label className="flex-1">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className="hidden"
                    disabled={loading}
                  />
                  <motion.div
                    className="flex items-center justify-center gap-2 px-4 py-2 border border-border rounded-lg cursor-pointer hover:bg-secondary/50 transition-colors"
                    whileHover={{ scale: loading ? 1 : 1.02 }}
                    whileTap={{ scale: loading ? 1 : 0.98 }}
                  >
                    <Upload className="w-4 h-4" />
                    <span>Choose Image...</span>
                  </motion.div>
                </label>
                {formData.logoUrl && (
                  <div className="flex items-center gap-2 px-3 py-1 bg-primary/10 text-primary rounded-full text-sm">
                    <span className="truncate max-w-xs">{formData.logoUrl}</span>
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, logoUrl: '' }))}
                      className="hover:bg-primary/20 rounded-full p-0.5 transition-colors"
                      disabled={loading}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                )}
              </div>
              {errors.logoUrl && (
                <p className="mt-1 text-sm text-destructive flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.logoUrl}
                </p>
              )}
              <p className="mt-1 text-sm text-muted-foreground">
                Upload a company logo (max 5MB, image files only)
              </p>
            </div>
          </div>

          {/* Business Details Section */}
          <div className="space-y-4 pt-4 border-t border-border">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              Business Details
            </h3>

            {/* Employee Count */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Employee Count <span className="text-destructive">*</span>
              </label>
              <input
                type="number"
                min="0"
                value={formData.employeeCount}
                onChange={(e) => handleInputChange('employeeCount', e.target.value ? parseInt(e.target.value, 10) : 0)}
                className={`w-full px-4 py-2 rounded-lg border bg-background ${
                  errors.employeeCount ? 'border-destructive' : 'border-border'
                } focus:outline-none focus:ring-2 focus:ring-primary`}
                placeholder="Enter number of employees"
                disabled={loading}
                required
              />
              {errors.employeeCount && (
                <p className="mt-1 text-sm text-destructive flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.employeeCount}
                </p>
              )}
              <p className="mt-1 text-sm text-muted-foreground">
                Number of employees in the company (required)
              </p>
            </div>

            {/* Revenue Range */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Revenue Range
              </label>
              <select
                value={formData.revenueRange ?? ''}
                onChange={(e) => handleInputChange('revenueRange', e.target.value || undefined)}
                className={`w-full px-4 py-2 rounded-lg border bg-background ${
                  errors.revenueRange ? 'border-destructive' : 'border-border'
                } focus:outline-none focus:ring-2 focus:ring-primary`}
                disabled={loading}
              >
                <option value="">Select revenue range</option>
                <option value="under-1M">Under $1M</option>
                <option value="1M-5M">$1M - $5M</option>
                <option value="5M-10M">$5M - $10M</option>
                <option value="10M-50M">$10M - $50M</option>
                <option value="50M-100M">$50M - $100M</option>
                <option value="100M+">$100M+</option>
              </select>
              {errors.revenueRange && (
                <p className="mt-1 text-sm text-destructive flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.revenueRange}
                </p>
              )}
              <p className="mt-1 text-sm text-muted-foreground">
                Annual revenue range (optional, helps us understand your company size)
              </p>
            </div>

            {/* AI Adoption Stage */}
            <div>
              <label className="block text-sm font-medium mb-2">
                AI Adoption Stage
              </label>
              <select
                value={formData.aiAdoptionStage ?? ''}
                onChange={(e) => handleInputChange('aiAdoptionStage', e.target.value || undefined)}
                className={`w-full px-4 py-2 rounded-lg border bg-background ${
                  errors.aiAdoptionStage ? 'border-destructive' : 'border-border'
                } focus:outline-none focus:ring-2 focus:ring-primary`}
                disabled={loading}
              >
                <option value="">Select adoption stage</option>
                <option value="exploring">Exploring</option>
                <option value="piloting">Piloting</option>
                <option value="implementing">Implementing</option>
                <option value="scaling">Scaling</option>
                <option value="mature">Mature</option>
              </select>
              {errors.aiAdoptionStage && (
                <p className="mt-1 text-sm text-destructive flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.aiAdoptionStage}
                </p>
              )}
              <p className="mt-1 text-sm text-muted-foreground">
                Current stage of AI adoption in your company
              </p>
            </div>
          </div>

          {/* AI Needs Section */}
          <div className="space-y-4 pt-4 border-t border-border">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              AI Needs & Use Cases
            </h3>

            {/* Business Branches */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Business Areas / Branches
              </label>
              <textarea
                value={formData.businessBranches}
                onChange={(e) => handleInputChange('businessBranches', e.target.value)}
                rows={4}
                className={`w-full px-4 py-2 rounded-lg border bg-background ${
                  errors.businessBranches ? 'border-destructive' : 'border-border'
                } focus:outline-none focus:ring-2 focus:ring-primary resize-none`}
                placeholder="Enter business areas or branches, one per line&#10;Example:&#10;Customer Support&#10;Sales&#10;Marketing&#10;Operations"
                disabled={loading}
              />
              {errors.businessBranches && (
                <p className="mt-1 text-sm text-destructive flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.businessBranches}
                </p>
              )}
              <p className="mt-1 text-sm text-muted-foreground">
                List the main business areas or branches in your company (optional)
              </p>
            </div>

            {/* AI Pain Points */}
            <div>
              <label className="block text-sm font-medium mb-2">
                AI Pain Points & Challenges <span className="text-destructive">*</span>
              </label>
              <textarea
                value={formData.aiPainPoints}
                onChange={(e) => handleInputChange('aiPainPoints', e.target.value)}
                rows={6}
                maxLength={2000}
                className={`w-full px-4 py-2 rounded-lg border bg-background ${
                  errors.aiPainPoints ? 'border-destructive' : 'border-border'
                } focus:outline-none focus:ring-2 focus:ring-primary resize-none`}
                placeholder="Describe the main challenges or pain points where you think AI could help your company.&#10;&#10;Examples:&#10;- Struggling to automate customer support responses&#10;- Need better insights from large datasets&#10;- Manual data entry is time-consuming&#10;- Difficulty predicting customer behavior&#10;- Inefficient document processing"
                disabled={loading}
              />
              {errors.aiPainPoints && (
                <p className="mt-1 text-sm text-destructive flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.aiPainPoints}
                </p>
              )}
              <p className="mt-1 text-sm text-muted-foreground">
                {formData.aiPainPoints?.length || 0} / 2000 characters
              </p>
              <p className="mt-1 text-sm text-muted-foreground">
                Please describe at least 20 characters. This helps us understand your AI needs better.
              </p>
            </div>
          </div>

          {/* Admin Account Section */}
          <div className="space-y-4 pt-4 border-t border-border">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <User className="w-5 h-5" />
              Admin Account
            </h3>
            <p className="text-sm text-muted-foreground">
              Create the admin account for this company. This user will have full access to manage the company.
            </p>

            {/* Admin Name (Required) */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Admin Name <span className="text-destructive">*</span>
              </label>
              <input
                type="text"
                value={formData.adminName}
                onChange={(e) => handleInputChange('adminName', e.target.value)}
                className={`w-full px-4 py-2 rounded-lg border bg-background ${
                  errors.adminName ? 'border-destructive' : 'border-border'
                } focus:outline-none focus:ring-2 focus:ring-primary`}
                placeholder="Enter admin's full name"
                disabled={loading}
              />
              {errors.adminName && (
                <p className="mt-1 text-sm text-destructive flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.adminName}
                </p>
              )}
            </div>

            {/* Admin Email (Required) */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Admin Email <span className="text-destructive">*</span>
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  type="email"
                  value={formData.adminEmail}
                  onChange={(e) => handleInputChange('adminEmail', e.target.value)}
                  className={`w-full pl-10 pr-4 py-2 rounded-lg border bg-background ${
                    errors.adminEmail ? 'border-destructive' : 'border-border'
                  } focus:outline-none focus:ring-2 focus:ring-primary`}
                  placeholder="admin@company.com"
                  disabled={loading}
                />
              </div>
              {errors.adminEmail && (
                <p className="mt-1 text-sm text-destructive flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.adminEmail}
                </p>
              )}
            </div>

            {/* Admin Password (Required) */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Admin Password <span className="text-destructive">*</span>
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.adminPassword}
                  onChange={(e) => handleInputChange('adminPassword', e.target.value)}
                  className={`w-full pl-10 pr-12 py-2 rounded-lg border bg-background ${
                    errors.adminPassword ? 'border-destructive' : 'border-border'
                  } focus:outline-none focus:ring-2 focus:ring-primary`}
                  placeholder="Enter password"
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  disabled={loading}
                >
                  {showPassword ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              </div>
              {errors.adminPassword && (
                <p className="mt-1 text-sm text-destructive flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.adminPassword}
                </p>
              )}
              <p className="mt-1 text-sm text-muted-foreground">
                Must be at least 8 characters long
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4 border-t border-border">
            <motion.button
              type="submit"
              disabled={loading}
              className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              whileHover={{ scale: loading ? 1 : 1.02 }}
              whileTap={{ scale: loading ? 1 : 0.98 }}
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Creating Company...
                </>
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  Create Company
                </>
              )}
            </motion.button>

            {onCancel && (
              <motion.button
                type="button"
                onClick={onCancel}
                disabled={loading}
                className="px-6 py-3 bg-secondary text-secondary-foreground rounded-lg font-semibold hover:bg-secondary/80 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                whileHover={{ scale: loading ? 1 : 1.02 }}
                whileTap={{ scale: loading ? 1 : 0.98 }}
              >
                Cancel
              </motion.button>
            )}
          </div>
        </form>
      </div>
    </motion.div>
  )
}
